import { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { ArrowLeft, Loader2, Package } from 'lucide-react';
import {
  fetchAdminOrder, formatCents, getDisplayErrorMessage, updateAdminOrderStatus,
  refundAdminOrder,
  type AdminOrderRow,
} from '@/lib/api';
import { useAuthStore } from '@/store/auth';
import { useRecentRoute } from '@/hooks/useRecentRoute';
import styles from './Orders.module.css';
import { SalesOrderAttribution } from './SalesOrderAttribution';
import { StoreFreightSection, type StoreFreight } from './StoreFreightSection';

interface OrderDetailPageProps {
  orderId: string;
  onNavigate: (path: string) => void;
}

const STATUS_LABELS: Record<string, string> = {
  paid: 'Paid',
  processing: 'Processing',
  shipped: 'Shipped',
  delivered: 'Delivered',
  refunded: 'Refunded',
  partially_refunded: 'Partially refunded',
  cancelled: 'Cancelled',
  pending: 'Pending',
};

/** Forward ranks for the buy-now lifecycle — mirrors the server's
 *  LIFECYCLE_RANK (apps/api/src/routes/admin/orders.ts). Statuses
 *  outside this map (pending, cancelled, quote motion) are read-only
 *  here; the PATCH 409s them anyway. */
const LIFECYCLE_RANK: Partial<Record<string, number>> = {
  paid: 1,
  processing: 2,
  shipped: 3,
  delivered: 4,
};

const LIFECYCLE_TARGETS = ['processing', 'shipped', 'delivered'] as const;
type LifecycleTarget = (typeof LIFECYCLE_TARGETS)[number];

type StatusPatchBody = Parameters<typeof updateAdminOrderStatus>[1];

function statusLabel(status: string): string {
  return STATUS_LABELS[status] ?? status;
}

/**
 * Admin store-order detail (store slice 4 client).
 *
 * Read surface: summary + totals, line items, shipping address, sales
 * partner (when attributed), the commissioning handoff, and the full
 * status history. Write surface: the lifecycle controls, mirroring
 * the server's rules so the operator sees the requirement before the
 * request fails:
 *
 *   • Mark shipped reveals a tracking-number input (required; the
 *     server 400s TRACKING_REQUIRED without one) + optional carrier.
 *   • Any move to a LOWER rank reveals a required reason textarea
 *     (server 400s REASON_REQUIRED) — backward moves are meant to be
 *     rare and audited, so the friction is intentional.
 *   • Statuses outside the lifecycle (pending / cancelled / quote
 *     motion) disable the controls entirely.
 *
 * Server errors surface verbatim via getDisplayErrorMessage — the
 * PATCH route writes user-facing copy for each rejection.
 */
export function OrderDetailPage({ orderId, onNavigate }: OrderDetailPageProps) {
  const queryClient = useQueryClient();

  const orderQuery = useQuery({
    queryKey: ['orders', 'detail', orderId],
    queryFn: () => fetchAdminOrder(orderId),
  });
  const order = orderQuery.data;

  // Detail-page recents: empty label until data resolves (the hook
  // no-ops on ''), then the order number.
  useRecentRoute(order ? `${order.order_number} (Order)` : '');

  if (orderQuery.isLoading && !order) {
    return <div className={styles.page}><p className={styles.loading}>Loading…</p></div>;
  }
  if (orderQuery.isError || !order) {
    const msg = orderQuery.error instanceof Error
      ? orderQuery.error.message
      : 'Order not found';
    return (
      <div className={styles.page}>
        <div className={styles.error}>{msg}</div>
        <button className={styles.backBtn} onClick={() => onNavigate('/orders')}>
          <ArrowLeft size={16} /> Back to Orders
        </button>
      </div>
    );
  }

  const hasSalesPartner = Boolean(order.sales_partner_name ?? order.sales_partner_email);

  return (
    <div className={styles.page}>
      <button className={styles.backBtn} onClick={() => onNavigate('/orders')}>
        <ArrowLeft size={16} /> Back to Orders
      </button>

      <div className={styles.header}>
        <div className={styles.headerLeft}>
          <div className={styles.headerIcon}><Package size={18} /></div>
          <div>
            <h1 className={styles.title}>Order {order.order_number}</h1>
            <p className={styles.subtitle}>
              {order.buyer_name ?? order.buyer_email ?? 'Unknown buyer'}
              {' · '}
              {new Date(order.created_at).toLocaleDateString()}
            </p>
          </div>
        </div>
        <span className={`${styles.statusBadge} ${styles[`status_${order.status}`] ?? ''}`}>
          {statusLabel(order.status)}
        </span>
      </div>

      <div className={styles.twoCol}>
        <div className={styles.sectionStack}>
          {/* Summary + totals */}
          <div className={styles.section}>
            <h2 className={styles.sectionTitle}>Summary</h2>
            <div className={styles.breakdownTable}>
              <div className={styles.breakdownRow}>
                <span>Subtotal</span>
                <span className={styles.amount}>{formatCents(order.subtotal_cents)}</span>
              </div>
              <div className={styles.breakdownRow}>
                <span>Shipping</span>
                <span className={styles.amount}>{formatCents(order.shipping_cents)}</span>
              </div>
              <div className={styles.breakdownRow}>
                <span>Tax</span>
                <span className={styles.amount}>{formatCents(order.tax_cents)}</span>
              </div>
              <div className={`${styles.breakdownRow} ${styles.totalRow}`}>
                <span>Total</span>
                <span className={styles.amount}>{formatCents(order.total_cents)}</span>
              </div>
            </div>
            <div className={styles.metaList}>
              <div><span className={styles.metaLabel}>Order number</span><span className={styles.mono}>{order.order_number}</span></div>
              <div><span className={styles.metaLabel}>Placed</span><span>{new Date(order.created_at).toLocaleString()}</span></div>
              <div><span className={styles.metaLabel}>Checkout flow</span><span>{order.checkout_flow}</span></div>
              <div><span className={styles.metaLabel}>Buyer</span><span>{order.buyer_name ?? '-'}</span></div>
              <div><span className={styles.metaLabel}>Buyer email</span><span className={styles.mono}>{order.buyer_email ?? '-'}</span></div>
              {order.tracking_number && (
                <div>
                  <span className={styles.metaLabel}>Tracking</span>
                  <span className={styles.mono}>
                    {order.tracking_number}
                    {order.tracking_carrier ? ` (${order.tracking_carrier})` : ''}
                  </span>
                </div>
              )}
              {order.shipped_at && (
                <div><span className={styles.metaLabel}>Shipped</span><span>{new Date(order.shipped_at).toLocaleString()}</span></div>
              )}
              {order.delivered_at && (
                <div><span className={styles.metaLabel}>Delivered</span><span>{new Date(order.delivered_at).toLocaleString()}</span></div>
              )}
              {order.notes && (
                <div><span className={styles.metaLabel}>Notes</span><span>{order.notes}</span></div>
              )}
            </div>
          </div>

          {/* Shipping address */}
          <div className={styles.section}>
            <h2 className={styles.sectionTitle}>Shipping address</h2>
            {order.shipping_address ? (
              <address className={styles.addressBlock}>
                {order.shipping_address.name}<br />
                {order.shipping_address.line1}<br />
                {order.shipping_address.line2 && <>{order.shipping_address.line2}<br /></>}
                {order.shipping_address.city}, {order.shipping_address.state}{' '}
                {order.shipping_address.postal_code}<br />
                {order.shipping_address.country}
              </address>
            ) : (
              <p className={styles.dimText}>No shipping address on this order.</p>
            )}
          </div>

          {/* Freight priced by Warp at checkout (Warren, 21 October 2026, evening, ruling 3) */}
          {(order as AdminOrderRow & { freight?: StoreFreight | null }).freight && <StoreFreightSection orderId={order.id}
            freight={(order as AdminOrderRow & { freight: StoreFreight }).freight} onDone={() => { void queryClient.invalidateQueries({ queryKey: ['orders'] }); }} />}

          {/* Sales partner attribution — only when the order carries one */}
          {hasSalesPartner && (
            <div className={styles.section}>
              <h2 className={styles.sectionTitle}>Sales partner</h2>
              <div className={styles.metaList} style={{ marginTop: 0, paddingTop: 0, borderTop: 'none' }}>
                <div><span className={styles.metaLabel}>Name</span><span>{order.sales_partner_name ?? '-'}</span></div>
                <div><span className={styles.metaLabel}>Email</span><span className={styles.mono}>{order.sales_partner_email ?? '-'}</span></div>
              </div>
            </div>
          )}

          <SalesOrderAttribution orderId={orderId} staffId={(order as AdminOrderRow & { sales_staff_id?: string | null }).sales_staff_id ?? null} />

          {/* Commissioning handoff. Shown for orders that hand off to
              commissioning (the legacy triggers_provisioning flag, or a
              commercial line on an older order the server reads at request
              time). The server state only knows about the legacy
              provisioning flow's tenant stamp; commissioning never sets it,
              so this section gives the steps, links the two pages they
              happen on, and points at Deployments for launch status. */}
          {order.provisioning_state !== 'not_required' && (
            <div className={styles.section}>
              <h2 className={styles.sectionTitle}>Commissioning</h2>
              {order.provisioning_state === 'pending' ? (
                <div className={styles.provisioningPending}>
                  Once the exact unit identities for order {order.order_number} are
                  known, open Submit a Location and register the deployment with
                  each unit's exact identity. Authorize commissioning on the
                  deployment page. The installer then uses the programming cards,
                  connection checks and label test, and an operator approves the
                  launch.
                </div>
              ) : (
                <div className={styles.provisioningDone}>
                  The older provisioning flow created a tenant for this order. Tenant:{' '}
                  <span className={styles.mono}>{order.provisioned_tenant_id ?? '-'}</span>
                </div>
              )}
              <p className={styles.dimText}>
                This page does not track commissioning. Check Deployment
                commissioning for launch status.
              </p>
              <div className={styles.controlsRow}>
                {order.provisioning_state === 'pending' && (
                  <button
                    type="button"
                    className={styles.actionBtn}
                    onClick={() => onNavigate('/submit-location')}
                  >
                    Open Submit a Location
                  </button>
                )}
                <button
                  type="button"
                  className={styles.actionBtn}
                  onClick={() => onNavigate('/deployments')}
                >
                  Open Deployment commissioning
                </button>
              </div>
            </div>
          )}
        </div>

        <div className={styles.sectionStack}>
          {/* Line items */}
          <div className={styles.section}>
            <h2 className={styles.sectionTitle}>Line items</h2>
            <table className={styles.itemsTable}>
              <thead>
                <tr>
                  <th>Item</th>
                  <th>Part number</th>
                  <th className={styles.alignRight}>Qty</th>
                  <th className={styles.alignRight}>Unit</th>
                  <th className={styles.alignRight}>Total</th>
                </tr>
              </thead>
              <tbody>
                {order.line_items.map((li) => (
                  <tr key={li.product_id}>
                     <td>{li.name}<div className={styles.dimText}>{li.cost_snapshot
                       ? `Recorded unit cost: ${formatCents(li.cost_snapshot.unit_cost_cents, li.cost_snapshot.currency)}; absorbed fee: ${formatCents(li.cost_snapshot.absorbed_autel_fee_cents, li.cost_snapshot.currency)}`
                       : 'Cost not recorded'}</div></td>
                    <td className={styles.mono}>{li.part_number}</td>
                    <td className={`${styles.alignRight} ${styles.mono}`}>{li.quantity}</td>
                    <td className={`${styles.alignRight} ${styles.mono}`}>{formatCents(li.unit_price_cents)}</td>
                    <td className={`${styles.alignRight} ${styles.amount}`}>{formatCents(li.line_total_cents)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          {/* Fulfillment — lifecycle controls + status history */}
          <div className={styles.section}>
            <h2 className={styles.sectionTitle}>Fulfillment</h2>
            <LifecycleControls order={order} onDone={() => {
              void queryClient.invalidateQueries({ queryKey: ['orders'] });
            }} />

            <h2 className={styles.sectionTitle}>Status history</h2>
            {order.status_history.length === 0 ? (
              <p className={styles.dimText}>No status changes yet.</p>
            ) : (
              <div className={styles.historyList}>
                {[...order.status_history].reverse().map((h, i) => (
                  <div key={i} className={styles.historyRow}>
                    <span>{statusLabel(h.from)} → {statusLabel(h.to)}</span>
                    <span className={styles.historyMeta}>{new Date(h.at).toLocaleString()}</span>
                    {h.reason && <span className={styles.historyReason}>Reason: {h.reason}</span>}
                  </div>
                ))}
              </div>
            )}
          </div>

          {/* Refund — guarded money control */}
          <div className={styles.section}>
            <h2 className={styles.sectionTitle}>Refund</h2>
            <RefundControl order={order} onDone={() => {
              void queryClient.invalidateQueries({ queryKey: ['orders'] });
            }} />
          </div>
        </div>
      </div>
    </div>
  );
}

// ── Refund control ───────────────────────────────────────────────────────
const REFUNDABLE_STATUSES = new Set([
  'paid', 'processing', 'shipped', 'delivered', 'partially_refunded',
]);
// Mirrors the server's default REFUND_SECOND_APPROVER_THRESHOLD_CENTS ($100).
// A hint only; the server is authoritative on whether a second approver is
// required.
const REFUND_SECOND_APPROVER_HINT_CENTS = 10_000;

interface RefundControlProps {
  order: AdminOrderRow;
  onDone: () => void;
}

function RefundControl({ order, onDone }: RefundControlProps) {
  const queryClient = useQueryClient();
  const userRole = useAuthStore((s) => s.user?.role);
  const isAdmin = userRole === 'super_admin' || userRole === 'tenant_admin';

  const [open, setOpen] = useState(false);
  const [reason, setReason] = useState('');
  const [partial, setPartial] = useState(false);
  const [amountDollars, setAmountDollars] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [result, setResult] = useState<string | null>(null);

  const refunded = order.refunded_cents ?? 0;
  const remaining = Math.max(0, order.total_cents - refunded);
  const effectiveCents = partial ? Math.round((Number.parseFloat(amountDollars) || 0) * 100) : remaining;
  const needsSecondApprover = effectiveCents >= REFUND_SECOND_APPROVER_HINT_CENTS;

  const mutation = useMutation({
    mutationFn: (vars: { reason: string; amountCents?: number }) =>
      refundAdminOrder(order.id, vars.reason, vars.amountCents),
    onSuccess: (res) => {
      setError(null);
      setResult(res.status === 'queued'
        ? (res.message ?? 'Queued for a second admin to approve.')
        : `Refunded ${formatCents(res.amount_cents)}.`);
      setOpen(false);
      setReason(''); setPartial(false); setAmountDollars('');
      void queryClient.invalidateQueries({ queryKey: ['orders'] });
      onDone();
    },
    onError: (err) => setError(
      err instanceof Error ? err.message : 'Refund failed. Try again or check the audit log.',
    ),
  });

  const submit = (): void => {
    const trimmed = reason.trim();
    if (!trimmed) { setError('Enter a reason for the refund.'); return; }
    let amountCents: number | undefined;
    if (partial) {
      const dollars = Number.parseFloat(amountDollars);
      if (!Number.isFinite(dollars) || dollars <= 0) { setError('Enter a valid partial amount.'); return; }
      amountCents = Math.round(dollars * 100);
      if (amountCents > remaining) { setError(`Amount exceeds the ${formatCents(remaining)} remaining.`); return; }
    }
    setError(null);
    mutation.mutate({ reason: trimmed, ...(amountCents !== undefined ? { amountCents } : {}) });
  };

  const inputStyle = {
    width: '100%', padding: '8px 10px', borderRadius: 6,
    border: '1px solid var(--evi-border)', background: 'var(--evi-surface)',
    color: 'var(--evi-text)', fontSize: 13,
  } as const;

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
      <p className={styles.dimText}>
        {refunded > 0
          ? `Refunded ${formatCents(refunded)} of ${formatCents(order.total_cents)}`
            + `${order.refund_status && order.refund_status !== 'none' ? ` (${order.refund_status})` : ''}.`
          : `Order total ${formatCents(order.total_cents)}. No refund issued.`}
      </p>
      {result && <p className={styles.dimText}>{result}</p>}
      {!isAdmin && <p className={styles.dimText}>Refunds require a super admin or tenant admin.</p>}
      {isAdmin && !REFUNDABLE_STATUSES.has(order.status) && (
        <p className={styles.dimText}>This order is not in a refundable state.</p>
      )}
      {isAdmin && REFUNDABLE_STATUSES.has(order.status) && remaining > 0 && !open && (
        <button
          type="button"
          onClick={() => { setOpen(true); setResult(null); setError(null); }}
          style={{
            alignSelf: 'flex-start', padding: '8px 14px', background: 'transparent',
            border: '1px solid var(--evi-warn, #f59e0b)', color: 'var(--evi-warn, #f59e0b)',
            borderRadius: 6, fontSize: 13, fontWeight: 600, cursor: 'pointer',
          }}
        >
          Refund order
        </button>
      )}
      {isAdmin && open && (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 8, maxWidth: 380 }}>
          <textarea
            value={reason}
            onChange={(e) => setReason(e.target.value)}
            rows={2}
            placeholder="Reason (required)"
            style={{ ...inputStyle, resize: 'vertical' }}
          />
          <label style={{ display: 'inline-flex', alignItems: 'center', gap: 6, fontSize: 13, color: 'var(--evi-text-mute)' }}>
            <input type="checkbox" checked={partial} onChange={(e) => setPartial(e.target.checked)} />
            Partial refund
          </label>
          {partial && (
            <input
              value={amountDollars}
              onChange={(e) => setAmountDollars(e.target.value)}
              inputMode="decimal"
              placeholder={`Amount in USD (up to ${formatCents(remaining)})`}
              style={inputStyle}
            />
          )}
          {needsSecondApprover && (
            <p className={styles.dimText}>This refund is $100 or more, so a second admin must approve it.</p>
          )}
          {error && <p style={{ color: 'var(--evi-danger, #ef4444)', fontSize: 13, margin: 0 }}>{error}</p>}
          <div style={{ display: 'flex', gap: 8 }}>
            <button
              type="button"
              disabled={mutation.isPending}
              onClick={submit}
              style={{
                padding: '8px 14px', background: 'var(--evi-warn, #f59e0b)', border: 'none',
                color: '#1a1a1a', borderRadius: 6, fontSize: 13, fontWeight: 600, cursor: 'pointer',
              }}
            >
              {mutation.isPending ? <Loader2 size={14} style={{ verticalAlign: 'middle' }} /> : 'Confirm refund'}
            </button>
            <button
              type="button"
              disabled={mutation.isPending}
              onClick={() => { setOpen(false); setError(null); }}
              style={{
                padding: '8px 14px', background: 'transparent',
                border: '1px solid var(--evi-border)', color: 'var(--evi-text)',
                borderRadius: 6, fontSize: 13, cursor: 'pointer',
              }}
            >
              Cancel
            </button>
          </div>
        </div>
      )}
    </div>
  );
}

// ── Lifecycle controls ───────────────────────────────────────────
//
// Inline rather than its own module — tightly coupled to the order
// detail page, same precedent as SessionDetailPage's RefundControl.
//
// Interaction model: a plain forward move (paid → processing,
// shipped → delivered) submits on click. Moves that need extra input
// open an inline panel first:
//
//   • target 'shipped' → tracking number (required) + carrier
//     (optional), prefilled from the order when re-shipping.
//   • any backward move → required reason textarea. A backward move
//     TO shipped shows both blocks.
//
// The optional PATCH keys are only included when set — the route
// schema rejects empty strings (minLength) and unknown keys.

interface LifecycleControlsProps {
  order: AdminOrderRow;
  /** Invalidate ['orders'] (list + detail) after a successful PATCH. */
  onDone: () => void;
}

function LifecycleControls({ order, onDone }: LifecycleControlsProps) {
  const [pendingTarget, setPendingTarget] = useState<LifecycleTarget | null>(null);
  const [trackingNumber, setTrackingNumber] = useState('');
  const [trackingCarrier, setTrackingCarrier] = useState('');
  const [reason, setReason] = useState('');
  const [actionError, setActionError] = useState<string | null>(null);

  const statusMutation = useMutation({
    mutationFn: (body: StatusPatchBody) => updateAdminOrderStatus(order.id, body),
    onSuccess: () => {
      onDone();
      setPendingTarget(null);
      setTrackingNumber('');
      setTrackingCarrier('');
      setReason('');
      setActionError(null);
    },
    onError: async (err) => {
      setActionError(
        await getDisplayErrorMessage(err, 'Status update failed. Please try again.'),
      );
    },
  });

  const currentRank = LIFECYCLE_RANK[order.status];
  const inLifecycle = currentRank !== undefined;

  const isBackward = (target: LifecycleTarget): boolean =>
    currentRank !== undefined && (LIFECYCLE_RANK[target] ?? 0) < currentRank;

  const startTransition = (target: LifecycleTarget) => {
    setActionError(null);
    if (target === 'shipped' || isBackward(target)) {
      // Needs extra input — open the panel, prefill tracking from the
      // order so a backward-then-forward reshipment keeps its number.
      setPendingTarget(target);
      setTrackingNumber(order.tracking_number ?? '');
      setTrackingCarrier(order.tracking_carrier ?? '');
      setReason('');
      return;
    }
    statusMutation.mutate({ status: target });
  };

  const submitPanel = () => {
    if (!pendingTarget) return;
    const needsTracking = pendingTarget === 'shipped';
    const needsReason = isBackward(pendingTarget);
    if (needsTracking && !trackingNumber.trim()) {
      setActionError('Tracking number is required to mark this order shipped.');
      return;
    }
    // Mirrors the server's minLength: 3 on reason, so the operator
    // sees the floor here instead of a generic 400.
    if (needsReason && reason.trim().length < 3) {
      setActionError('A reason of at least 3 characters is required to move this order backward.');
      return;
    }
    setActionError(null);
    const body: StatusPatchBody = { status: pendingTarget };
    if (needsTracking) {
      body.tracking_number = trackingNumber.trim();
      if (trackingCarrier.trim()) body.tracking_carrier = trackingCarrier.trim();
    }
    if (needsReason) body.reason = reason.trim();
    statusMutation.mutate(body);
  };

  return (
    <div>
      <div className={styles.controlsRow}>
        {LIFECYCLE_TARGETS.map((target) => (
          <button
            key={target}
            type="button"
            className={styles.actionBtn}
            disabled={!inLifecycle || order.status === target || statusMutation.isPending}
            onClick={() => startTransition(target)}
          >
            Mark {target}
          </button>
        ))}
      </div>

      {!inLifecycle && (
        <p className={styles.controlsHint} style={{ marginTop: 8 }}>
          This order&apos;s status ({statusLabel(order.status)}) is outside the
          buy-now lifecycle, so it cannot be advanced here.
        </p>
      )}

      {pendingTarget && (
        <div className={styles.controlPanel} style={{ marginTop: 10 }}>
          {pendingTarget === 'shipped' && (
            <>
              <label className={styles.panelLabel} htmlFor="order-tracking-number">
                Tracking number (required)
              </label>
              <input
                id="order-tracking-number"
                className={styles.input}
                value={trackingNumber}
                onChange={(e) => setTrackingNumber(e.target.value)}
                maxLength={120}
                disabled={statusMutation.isPending}
                placeholder="e.g. 1Z999AA10123456784"
              />
              <label className={styles.panelLabel} htmlFor="order-tracking-carrier">
                Carrier (optional)
              </label>
              <input
                id="order-tracking-carrier"
                className={styles.input}
                value={trackingCarrier}
                onChange={(e) => setTrackingCarrier(e.target.value)}
                maxLength={60}
                disabled={statusMutation.isPending}
                placeholder="e.g. UPS"
              />
            </>
          )}
          {isBackward(pendingTarget) && (
            <>
              <label className={styles.panelLabel} htmlFor="order-status-reason">
                Reason (required, recorded in status history)
              </label>
              <textarea
                id="order-status-reason"
                className={styles.textarea}
                value={reason}
                onChange={(e) => setReason(e.target.value)}
                rows={3}
                maxLength={500}
                disabled={statusMutation.isPending}
                placeholder="e.g. Carrier returned the package, reshipping"
              />
            </>
          )}
          <div className={styles.panelActions}>
            <button
              type="button"
              className={styles.cancelBtn}
              disabled={statusMutation.isPending}
              onClick={() => {
                setPendingTarget(null);
                setActionError(null);
              }}
            >
              Cancel
            </button>
            <button
              type="button"
              className={styles.confirmBtn}
              disabled={statusMutation.isPending}
              onClick={submitPanel}
            >
              {statusMutation.isPending && <Loader2 size={14} className={styles.spin} />}
              {statusMutation.isPending ? 'Saving…' : `Mark ${pendingTarget}`}
            </button>
          </div>
        </div>
      )}

      {actionError && (
        <div className={styles.error} style={{ marginTop: 10 }}>{actionError}</div>
      )}
    </div>
  );
}
